import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MapPin, MessageSquare, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSiteContent } from '../hooks/useSiteContent';
import { RatingBadge } from '../components/RatingBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public Reviews Wall (/reviews) — latest reviews across all hotels.
 */
export const ReviewsPage = () => {
  const { user } = useAuth();
  const { sections: c } = useSiteContent('reviews');
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState(0); // 0 = all, 5/4/3/2/1 = exact
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async (minRating) => {
    setLoading(true);
    try {
      const params = minRating > 0 ? `?min_rating=${minRating}` : '';
      const { data } = await axios.get(`${API}/guest/reviews/all${params}`);
      setReviews(data.reviews || []);
    } catch (e) {
      toast.error('Failed to load reviews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews(filter);
  }, [filter, fetchReviews]);

  const avg = reviews.length > 0
    ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
    : '—';

  return (
    <div className="min-h-screen bg-white" data-testid="reviews-page">
      {/* HERO */}
      <section className="relative pt-12 pb-12 bg-gradient-to-b from-[#FFF5F8] via-white to-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-[#FF4D8A] rounded-full opacity-10 blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#FFE5EE] text-[#FF0059] text-xs font-bold mb-3">
            <MessageSquare className="h-3.5 w-3.5" /> GUEST REVIEWS
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-black mb-3 leading-[1.05]" data-testid="reviews-title">
            {c.hero_title || 'What travellers are saying'}
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-2xl">
            {c.hero_subtitle || 'Real reviews from real stays across Punjab, Haryana & Chandigarh.'}
          </p>
          <div className="mt-6 flex items-center gap-6">
            <div data-testid="reviews-avg">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Avg rating</p>
              <p className="text-3xl font-black text-black flex items-center gap-2">
                {avg}<span className="text-base text-gray-400">/10</span>
              </p>
            </div>
            <div data-testid="reviews-count">
              <p className="text-xs font-bold uppercase tracking-wider text-gray-500">Total reviews</p>
              <p className="text-3xl font-black text-black">{reviews.length}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-6">
        <div className="flex flex-wrap items-center gap-2" data-testid="reviews-filter-row">
          {[
            { id: 0, label: 'All ratings' },
            { id: 9, label: '9+ Exceptional' },
            { id: 8, label: '8+ Very Good' },
            { id: 7, label: '7+ Good' },
            { id: 6, label: '6+ Pleasant' },
          ].map((opt) => (
            <button
              key={opt.id}
              onClick={() => setFilter(opt.id)}
              className={`px-4 py-2 rounded-full text-xs font-bold border-2 transition-colors ${
                filter === opt.id
                  ? 'bg-[#FF0059] text-white border-[#FF0059]'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-[#FF0059]'
              }`}
              data-testid={`review-filter-${opt.id}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      {/* WALL */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="reviews-wall">
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-44 bg-gray-100 animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16 bg-[#FAFAFA] rounded-2xl" data-testid="reviews-empty">
            <Sparkles className="h-10 w-10 mx-auto text-[#FF0059] mb-3" />
            <p className="text-gray-600 font-bold">No reviews yet for this filter.</p>
            <button onClick={() => setFilter(0)} className="mt-3 text-sm font-bold text-[#FF0059] hover:underline">Reset filter</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {reviews.map((r) => (
              <article
                key={r._id}
                className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#FF0059] hover:shadow-md transition-all flex flex-col"
                data-testid={`review-card-${r._id}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-[#FF0059] text-white flex items-center justify-center text-sm font-black flex-shrink-0">
                    {(r.reviewer_first_name || 'G').charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm text-black truncate">{r.reviewer_first_name || 'Guest'}</p>
                    <div className="mt-0.5">
                      <RatingBadge value={r.rating} size="sm" showLabel={false} testId={`review-rate-${r._id}`} />
                    </div>
                  </div>
                </div>
                {r.comment && <p className="text-sm text-gray-700 leading-relaxed line-clamp-4 flex-1">{r.comment}</p>}
                {r.hotel && (r.hotel.hotel_name || r.hotel.city_area) && (
                  <div className="mt-3 pt-3 border-t border-gray-100 flex items-center gap-2">
                    {r.hotel.image && (
                      <img src={r.hotel.image} alt="" className="w-10 h-10 rounded-lg object-cover bg-gray-100" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-xs font-black text-black truncate" data-testid={`review-hotel-${r._id}`}>{r.hotel.hotel_name}</p>
                      <p className="text-[11px] text-gray-500 flex items-center gap-0.5 truncate">
                        <MapPin className="h-3 w-3" /> {r.hotel.city_area}
                      </p>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#FF0059] to-[#CC0047] p-10 md:p-14 text-white text-center" data-testid="reviews-cta">
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">{c.cta_title || 'Earn ₹20 for an honest review'}</h2>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            {c.cta_subtitle || 'Rate above 6 / 10 on a completed booking and get ₹10 instantly. Add a thoughtful comment without negative words to earn ₹10 more.'}
          </p>
          <Link
            to={user?.role === 'guest' ? '/my-bookings' : '/login'}
            className="inline-block bg-white text-[#FF0059] hover:bg-[#FFE5EE] font-black px-8 py-4 rounded-full transition-colors shadow-xl"
            data-testid="reviews-cta-btn"
          >
            {user?.role === 'guest' ? 'Rate a past stay →' : 'Sign in to review →'}
          </Link>
        </div>
      </section>
    </div>
  );
};
